import { getTransactionReceipt } from '@wagmi/core'

import {
    BACKOFF_MULTIPLIER,
    INITIAL_POLLING_INTERVAL,
    MAXIMUM_RETRY_COUNT,
    MAX_POLLING_INTERVAL,
} from '@src/constants/exchange'
import { ResultCode } from '@src/enums'
import { getWagmiConfig } from '@src/providers/Wallet/EVMProvider'
import { exchangeLogger } from '@src/utils/logger'
import { useHistoryStore } from '@src/zustand_stores/history.store'

export const useWaitForTransactionEvm = () => {
    const updateHistory = useHistoryStore(state => state.updateHistory)

    const waitForTransactionReceipt = async (hash: `0x${string}`) => {
        return new Promise((resolve, reject) => {
            // Pass the retryCount as a parameter to track it properly in the recursive function
            const attemptGetReceipt = async (currentRetryCount: number) => {
                if (currentRetryCount >= MAXIMUM_RETRY_COUNT) {
                    updateHistory(null, {
                        result: {
                            sourceTxHash: ``,
                            message: 'We are so sorry that this order failed. Please try again.',
                            status: ResultCode.failure,
                        },
                    })
                    reject({ message: 'Exceeded maximum retry count' })
                    return
                }

                try {
                    // Get the config from EVMProvider to ensure consistency
                    const config = getWagmiConfig()
                    if (!config) {
                        throw new Error('Wagmi config is not available - EVMProvider may not be initialized properly')
                    }

                    // Log transaction details to help troubleshoot issues with specific networks like Sepolia
                    exchangeLogger.info('Waiting for transaction receipt:', {
                        transactionHash: hash,
                        retryCount: currentRetryCount,
                    })

                    const receipt = await getTransactionReceipt(config, { hash })

                    exchangeLogger.info('Transaction receipt:', { receipt })

                    if (receipt) {
                        resolve(receipt)
                        return
                    }

                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, currentRetryCount),
                        MAX_POLLING_INTERVAL
                    )
                    const nextRetryCount = currentRetryCount + 1
                    setTimeout(() => attemptGetReceipt(nextRetryCount), nextInterval)
                } catch (error) {
                    // Enhance error logging with more context
                    exchangeLogger.error(`getTransactionReceipt error: \n${(error as Error).message}`, {
                        error,
                        hash,
                        retryCount: currentRetryCount,
                    })

                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, currentRetryCount),
                        MAX_POLLING_INTERVAL
                    )
                    const nextRetryCount = currentRetryCount + 1
                    setTimeout(() => attemptGetReceipt(nextRetryCount), nextInterval)
                }
            }

            attemptGetReceipt(0)
        })
    }

    // No need for useEffect to reset retry count anymore, as we're using function parameters
    return { waitForTransactionReceipt }
}
